import { courses, type Course } from "@/content/courses";

export interface CourseProblem {
  id: string;
  /** Short, plain-spoken label shown in the picker. */
  label: string;
  /** Leader Behaviors most relevant to the problem. */
  behaviors: string[];
  /** Words and phrases that appear in the courses that help. */
  keywords: string[];
}

/**
 * Common organizational problems, mapped to the Leader Behaviors and course
 * language that address them. Used by the course catalog problem filter.
 */
export const courseProblems: CourseProblem[] = [
  {
    id: "new-supervisors",
    label: "New supervisors were promoted without training",
    behaviors: ["Builds Trust", "Sets Goals", "Communicates", "Diagnoses Needs"],
    keywords: [
      "new leader",
      "new supervisor",
      "first-time",
      "new manager",
      "boot camp",
      "transition",
      "supervisor",
      "leadership essentials",
      "fundamentals",
    ],
  },
  {
    id: "accountability",
    label: "Nobody is held accountable for commitments",
    behaviors: ["Focuses on Results through People", "Sets Goals", "Builds Trust"],
    keywords: [
      "accountability",
      "accountable",
      "expectations",
      "follow through",
      "performance",
      "consequences",
      "commitment",
      "results",
    ],
  },
  {
    id: "difficult-conversations",
    label: "Managers avoid hard conversations",
    behaviors: ["Communicates", "Builds Trust", "Demonstrates Self-Management"],
    keywords: [
      "difficult conversation",
      "conflict",
      "feedback",
      "coaching conversation",
      "confront",
      "candor",
      "crucial",
      "disagree",
    ],
  },
  {
    id: "turnover",
    label: "Good people keep quitting",
    behaviors: ["Builds Trust", "Inspires", "Pursues Self-Development", "Diagnoses Needs"],
    keywords: [
      "retention",
      "engagement",
      "turnover",
      "psychological safety",
      "trust",
      "motivation",
      "recognition",
      "stay interview",
      "onboarding",
    ],
  },
  {
    id: "burnout",
    label: "Managers are overloaded and burning out",
    behaviors: ["Demonstrates Self-Management", "Focuses on Results through People"],
    keywords: [
      "burnout",
      "stress",
      "time management",
      "prioritize",
      "priorities",
      "delegat",
      "workload",
      "resilience",
      "balance",
    ],
  },
  {
    id: "delegation",
    label: "Leaders do the work instead of delegating",
    behaviors: ["Focuses on Results through People", "Pursues Self-Development", "Sets Goals"],
    keywords: ["delegat", "empower", "development plan", "coaching", "let go", "ownership"],
  },
  {
    id: "inconsistency",
    label: "Every manager leads differently",
    behaviors: ["Matches Style to Needs", "Diagnoses Needs", "Communicates"],
    keywords: [
      "consistent",
      "leadership style",
      "adapting",
      "situational",
      "standard",
      "common language",
      "align",
    ],
  },
  {
    id: "teamwork",
    label: "Teams and departments do not work together",
    behaviors: ["Collaborates and Promotes Teamwork", "Communicates", "Leverages Diversity"],
    keywords: [
      "team",
      "collaborat",
      "silo",
      "cross-functional",
      "meeting",
      "influence",
      "partnership",
    ],
  },
  {
    id: "change",
    label: "People resist change",
    behaviors: ["Champions Change", "Communicates", "Inspires"],
    keywords: ["change", "resistance", "uncertainty", "transition", "vision", "buy-in"],
  },
  {
    id: "communication",
    label: "Direction gets lost between levels",
    behaviors: ["Communicates", "Sets Goals", "Focuses on Results through People"],
    keywords: [
      "communicat",
      "listening",
      "message",
      "clarity",
      "presentation",
      "writing",
      "one-on-one",
    ],
  },
  {
    id: "performance-reviews",
    label: "Performance reviews are a surprise",
    behaviors: ["Sets Goals", "Communicates", "Diagnoses Needs"],
    keywords: [
      "performance review",
      "appraisal",
      "goal",
      "documentation",
      "feedback",
      "expectations",
      "progress",
    ],
  },
  {
    id: "problem-solving",
    label: "Problems come to you instead of getting solved",
    behaviors: ["Solves Problems", "Takes Initiative", "Innovates"],
    keywords: [
      "problem solving",
      "decision",
      "critical thinking",
      "root cause",
      "initiative",
      "innovat",
    ],
  },
  {
    id: "self-awareness",
    label: "Leaders do not see how they land on people",
    behaviors: ["Practices Self-Awareness", "Demonstrates Self-Management", "Builds Trust"],
    keywords: [
      "self-awareness",
      "emotional intelligence",
      "empathy",
      "blind spot",
      "presence",
      "mindset",
    ],
  },
  {
    id: "inclusion",
    label: "People do not feel heard or included",
    behaviors: ["Leverages Diversity", "Builds Trust", "Collaborates and Promotes Teamwork"],
    keywords: ["diversity", "inclusion", "belonging", "bias", "respect", "generation", "culture"],
  },
];

function courseText(c: Course) {
  return [
    c.title,
    c.tagline ?? "",
    c.whoFor ?? "",
    c.overview ?? "",
    ...(c.outcomes ?? []),
    ...(c.behaviors ?? []),
    ...(c.sessions ?? []).flatMap((s) => [s.title, s.body]),
  ]
    .join(" ")
    .toLowerCase();
}

const textIndex = new Map<string, string>(courses.map((c) => [c.slug, courseText(c)]));

function scoreCourse(c: Course, keywords: string[], behaviors: string[]) {
  const title = c.title.toLowerCase();
  const body = textIndex.get(c.slug) ?? "";
  let keywordScore = 0;
  for (const raw of keywords) {
    const k = raw.trim().toLowerCase();
    if (!k) continue;
    if (title.includes(k)) keywordScore += 3;
    else if (body.includes(k)) keywordScore += 1;
  }
  const behaviorOverlap = (c.behaviors ?? []).filter((b) => behaviors.includes(b)).length;
  return { keywordScore, behaviorOverlap, total: keywordScore + behaviorOverlap };
}

/** Courses that help with a mapped problem, best matches first. */
export function coursesForProblem(problemId: string): Course[] {
  const problem = courseProblems.find((p) => p.id === problemId);
  if (!problem) return [];
  return courses
    .map((c) => ({ c, s: scoreCourse(c, problem.keywords, problem.behaviors) }))
    .filter(
      ({ s }) =>
        s.keywordScore >= 3 ||
        (s.keywordScore >= 1 && s.behaviorOverlap >= 2) ||
        s.behaviorOverlap >= 3,
    )
    .sort((a, b) => b.s.total - a.s.total || a.c.title.localeCompare(b.c.title))
    .map(({ c }) => c);
}

/**
 * Free-text problem search. Matches the words a person types against course
 * language, plus the keywords of any mapped problem their words touch.
 */
export function coursesForProblemText(input: string): Course[] {
  const text = input.trim().toLowerCase();
  if (!text) return [];
  const words = text.split(/[^a-z0-9']+/).filter((w) => w.length > 3);
  const terms = new Set<string>([text, ...words]);

  const matchedBehaviors = new Set<string>();
  for (const problem of courseProblems) {
    const hit =
      problem.keywords.some((k) => text.includes(k.toLowerCase())) ||
      problem.label
        .toLowerCase()
        .split(/[^a-z]+/)
        .some((w) => w.length > 4 && terms.has(w));
    if (hit) {
      problem.keywords.forEach((k) => terms.add(k.toLowerCase()));
      problem.behaviors.forEach((b) => matchedBehaviors.add(b));
    }
  }

  const behaviors = Array.from(matchedBehaviors);
  return courses
    .map((c) => ({ c, s: scoreCourse(c, Array.from(terms), behaviors) }))
    .filter(({ s }) => s.keywordScore >= 2)
    .sort((a, b) => b.s.total - a.s.total || a.c.title.localeCompare(b.c.title))
    .map(({ c }) => c);
}
